import { DashboardFolderT, DashboardT } from 'business/definitions';
import { getSearchBarFilter } from 'routes/AllDashboards/helpers/filterDashboardsList';

/**
 * Filters a list of dashboards based on a search value if searching is active.
 *
 * @param dashboardsList - The list of dashboards to filter.
 * @param searchValue - The search string used to filter the dashboards.
 * @param filterOutFolders - A boolean indicating whether to filter out folders
 *                          when the search value is empty.
 * @returns The filtered list of dashboards if searching is active, otherwise the original list.
 */
export const filterDashboardsBySearch = (
    dashboardsList: DashboardT[],
    searchValue: string,
    filterOutFolders: boolean = false,
) =>
    dashboardsList.filter((dashboard) => {
        if (searchValue !== '') {
            return getSearchBarFilter(searchValue)(dashboard);
        } else {
            return filterOutFolders ? !dashboard.folder : true;
        }
    });

/**
 * Updates the list of dashboard folders by mapping each folder to include updated dashboards
 * and filtering out folders that have no dashboards. The resulting folders are sorted
 * alphabetically by their name.
 *
 * @param dashboardFolders - An array of dashboard folder objects to be updated.
 * @param dashboards - An array of dashboards to match and update within the folders.
 * @returns A new array of updated and sorted dashboard folders containing the full Dashboard.
 */
export const updateDashboardFolders = (
    dashboardFolders: DashboardFolderT[],
    dashboards: DashboardT[],
) => {
    return dashboardFolders
        .map((folder) => ({
            ...folder,
            dashboards: folder.dashboards
                .map((fd) => dashboards.find((d) => d.key === fd.key) || null)
                .filter(Boolean) as DashboardT[],
        }))
        .filter((folder) => folder.dashboards.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
};
